const GENERIC_FAILURE_MESSAGE =
  "Payment couldn't be completed. Check its status before trying again if your account was debited.";

const UNVERIFIED_MESSAGE =
  "We couldn't verify this payment yet. Check its status before paying again. If your account was debited and the status doesn't update, contact support.";

export const PENDING_ATTEMPT_MESSAGE =
  "Your last payment attempt is still pending. Check its status before paying again.";

export const PAYMENT_CONFIRMATION_PENDING_MESSAGE =
  "Payment was approved and is being confirmed.";

export const PAYMENT_REFUNDED_MESSAGE =
  "Your payment was refunded. This booking is not confirmed. Start a new booking to try again; your bank may take time to show the refund.";

/**
 * The UPI QR drawer's way of saying "I am done looking, you take it from here".
 *
 * A QR payment is settled by the `qr_code.credited` webhook, not by anything the
 * browser can observe: the payment arrives with `order_id: null` and is stitched
 * to the booking by its notes, server-side. So when the drawer closes it has no
 * outcome to report — only the knowledge that money may be on its way. Routing
 * that through `isPaymentOutcomeUnknown` hands it to the page's existing pending
 * poll, which is the same path an abandoned collect already takes, instead of
 * inventing a second settlement mechanism next to the one that works.
 */
export const UPI_QR_AWAITING_MESSAGE =
  "Waiting for your UPI payment to be confirmed.";

/**
 * No UPI app opened, and why that is its own outcome.
 *
 * Razorpay's mobile Intent screen lists the top PSP apps irrespective of
 * whether they are installed, so tapping a tile for an app you do not have is
 * an ordinary, frequent event rather than an edge case — and it is the single
 * most common way a mobile-web checkout dies. The provider reports it as
 * `reason: "intent_no_apps_error"`. On Safari that reason is ambiguous: it is
 * also emitted when the payer declines the browser's confirmation to open an
 * installed app. It is still precise enough to know that no payment started,
 * but never precise enough to hide the app. Recovery therefore keeps every
 * method visible and lets the payer choose the same app again.
 */
export const UPI_NO_APP_MESSAGE =
  "The UPI app didn't open — the prompt may have been cancelled or the app may be unavailable. Choose it again or use another method.";

/**
 * Did the app handoff stop before a payment was initiated?
 *
 * Matches on the provider's machine reason first, and falls back to its
 * description because the same condition reaches us worded two ways: the
 * structured `payment.failed` payload carries the reason, while the SDK's own
 * on-screen copy ("No UPI App on this device") is what surfaces when only a
 * message survives.
 */
export function isUpiNoAppFailure(input: {
  reason?: string | null;
  message?: string | null;
}): boolean {
  if (input.reason && /intent_no_apps_error/i.test(input.reason)) return true;
  const message = input.message?.trim();
  if (!message) return false;
  return (
    /no upi app/i.test(message) ||
    /failed with selected app/i.test(message) ||
    /unable to open upi app/i.test(message)
  );
}

export const CANCEL_TIMEOUT_MESSAGE =
  "Couldn't cancel the booking. Check your connection and try again.";

/**
 * Did we give up on this payment without ever learning its outcome?
 *
 * Distinguishes "the bank declined it" — terminal, retry is the right offer —
 * from "we stopped waiting", where the collect request may still be live and the
 * money may still arrive. Retrying the second kind can double-charge somebody
 * who is mid-approval, so those screens poll for the real answer instead.
 */
export function isPaymentOutcomeUnknown(raw: string): boolean {
  const message = raw.trim();
  return (
    /payment did not respond/i.test(message) ||
    /payment was not completed/i.test(message) ||
    /payment attempt is already being prepared/i.test(message) ||
    /payment (?:has )?already been processed/i.test(message) ||
    /another payment attempt is still active/i.test(message) ||
    message === PAYMENT_CONFIRMATION_PENDING_MESSAGE ||
    message === PENDING_ATTEMPT_MESSAGE ||
    /unsupported razorpay payment status/i.test(message) ||
    /no razorpay payment was found/i.test(message) ||
    /has not activated this subscription yet/i.test(message) ||
    message === UPI_QR_AWAITING_MESSAGE
  );
}

/**
 * Internal/provider error strings must never reach users verbatim. Known
 * internal patterns are replaced; anything else (e.g. Razorpay's own
 * human-readable failure descriptions like "Payment declined by bank") is
 * passed through.
 */
export function toFriendlyPaymentMessage(raw: string): string {
  const message = raw.trim();
  if (!message) return GENERIC_FAILURE_MESSAGE;

  if (
    /payment (?:has )?already been processed/i.test(message) ||
    /another payment attempt is still active/i.test(message)
  ) {
    return "This payment may already have gone through. We're checking its status — don't pay again.";
  }

  if (/unsupported razorpay payment status/i.test(message)) {
    return PENDING_ATTEMPT_MESSAGE;
  }
  if (/payment attempt is already being prepared/i.test(message)) {
    return PENDING_ATTEMPT_MESSAGE;
  }
  if (/no razorpay payment was found/i.test(message)) {
    return PENDING_ATTEMPT_MESSAGE;
  }
  if (/payment did not respond/i.test(message)) {
    return "We haven't received the final payment status yet. We're checking it now. Don't pay again while we confirm it.";
  }
  if (
    /payment was not completed/i.test(message) ||
    message === PAYMENT_CONFIRMATION_PENDING_MESSAGE
  ) {
    return "Your payment may have been approved. We're confirming it now — don't pay again.";
  }
  if (isUpiNoAppFailure({ message })) {
    return UPI_NO_APP_MESSAGE;
  }
  if (message === UPI_QR_AWAITING_MESSAGE) {
    return "If you completed the payment in your UPI app it will confirm here shortly — otherwise scan again below.";
  }
  if (/signature/i.test(message)) {
    return UNVERIFIED_MESSAGE;
  }
  if (/(order|amount|currency) mismatch/i.test(message)) {
    return UNVERIFIED_MESSAGE;
  }
  // Raw provider API failures, e.g. "Razorpay order failed (400): {...}"
  if (/razorpay .* \(\d{3}\)/i.test(message) || message.includes("{")) {
    return "The payment service had a problem. Please try again in a moment.";
  }

  return message;
}
